/**
 * Diagnostic Question Templates
 * These templates are hydrated with state-specific data to generate questions for all 50 states + DC
 */

import type { QuestionTemplate } from '../../types/diagnostic';

// ============================================
// SECTION A: LICENSING & OPERATIONS
// ============================================

export const TEMPLATE_A1_LICENSE_STATUS: QuestionTemplate = {
  itemId: 'A1',
  templateId: 'license-status',
  title: '{agencyAbbr} License Status',
  description: 'Verify your home care organization license is current and active',
  severity: 'high',

  questions: [
    {
      id: 'q1',
      question: 'Do you have a current, active license from {agency} to operate as a home care organization?',
      inputType: 'binary',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'What is your license expiration date?',
      inputType: 'date',
      helpText: 'Enter the expiration date shown on your current state provider license',
      showCondition: {
        questionId: 'q1',
        operator: 'equals',
        value: 'yes',
      },
    },
  ],

  scoringRules: [
    // No license = Non-Compliant
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    // License expires in 90+ days = Compliant (adjusted per state)
    { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
    // License expires in 30-90 days = At Risk
    { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
    // License expires in <30 days or expired = Non-Compliant
    { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Your {agencyAbbr} home care organization license is current and active. No action required.',
    atRisk: 'Your license expires within {warningDays} days. {state} requires renewal applications to be submitted before expiration. Submit your renewal to {agencyAbbr} immediately to avoid lapse in licensure.',
    nonCompliant: 'Operating without a valid {agencyAbbr} license is a violation of {state} law. You must have a valid license to provide services. Contact {agencyAbbr} immediately{phone}.',
    unknown: 'Unable to determine license status. Verify your license expiration date with {agencyAbbr}.',
  },
};

export const TEMPLATE_A2_LICENSE_ACCESSIBILITY: QuestionTemplate = {
  itemId: 'A2',
  templateId: 'license-accessibility',
  title: 'License Accessibility for Inspection',
  description: 'License must be available for inspection',
  severity: 'medium',

  questions: [
    {
      id: 'q1',
      question: 'Where is your {agencyAbbr} license currently kept?',
      inputType: 'select',
      options: [
        { value: 'posted', label: 'Posted on the wall in our main office (visible to visitors)' },
        { value: 'digital_quick', label: 'Stored digitally and can be retrieved within 60 seconds' },
        { value: 'filed', label: 'Filed in a cabinet or drawer' },
        { value: 'unknown', label: 'Not sure where it is' },
        { value: 'virtual', label: 'We operate virtually / no physical office' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'equals', value: 'posted', result: 'COMPLIANT' },
    { condition: 'equals', value: 'digital_quick', result: 'COMPLIANT' },
    { condition: 'equals', value: 'virtual', result: 'COMPLIANT' },
    { condition: 'equals', value: 'filed', result: 'AT_RISK' },
    { condition: 'equals', value: 'unknown', result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Your license is readily available for inspection as required by {state} regulations.',
    atRisk: 'Your license should be conspicuously posted or immediately retrievable. {agencyAbbr} surveyors expect to see it within seconds of arrival. Move your license to a visible location or create a system for instant access.',
    nonCompliant: 'You must be able to produce your license immediately upon request. Locate your license today and post it conspicuously or set up instant retrieval. Inability to produce a license during survey is a deficiency.',
    unknown: 'Verify your license location and ensure it is accessible for inspection.',
  },
};

export const TEMPLATE_A3_OWNERSHIP_INFO: QuestionTemplate = {
  itemId: 'A3',
  templateId: 'ownership-info',
  title: 'Ownership/Administrator Information',
  description: 'Ownership and administrator changes must be reported to the state',
  severity: 'medium',

  questions: [
    {
      id: 'q1',
      question: 'Has there been any change in ownership or administrator at your agency in the past 12 months?',
      inputType: 'select',
      options: [
        { value: 'no_changes', label: 'No changes' },
        { value: 'admin_only', label: 'Yes — change in administrator only' },
        { value: 'ownership', label: 'Yes — change in ownership' },
        { value: 'both', label: 'Yes — both ownership and administrator changed' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Was {agencyAbbr} notified of this change?',
      inputType: 'select',
      options: [
        { value: 'within_30', label: 'Yes, within 30 days of the change' },
        { value: 'after_30', label: 'Yes, but more than 30 days after the change' },
        { value: 'not_notified', label: 'No, {agencyAbbr} has not been notified' },
        { value: 'unsure', label: 'Not sure' },
      ],
      showCondition: {
        questionId: 'q1',
        operator: 'notEquals',
        value: 'no_changes',
      },
    },
  ],

  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'no_changes', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q2', value: 'within_30', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q2', value: 'after_30', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q2', value: 'not_notified', result: 'NON_COMPLIANT' },
    { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'AT_RISK' },
  ],

  reportLanguage: {
    compliant: 'Your ownership and administrator information on file with {agencyAbbr} is current.',
    atRisk: 'Administrator changes must be reported within 30 days per {state} regulations. Your late notification may be noted during survey. Ensure all current information is on file with {agencyAbbr}.',
    nonCompliant: 'Unreported changes violate {state} regulations. If ownership changed, you may be operating under an invalid license — contact {agencyAbbr} immediately. For administrator changes, submit written notification today.',
    unknown: 'Verify that your current ownership and administrator information is on file with {agencyAbbr}.',
  },
};

export const TEMPLATE_A4_POLICIES_PROCEDURES: QuestionTemplate = {
  itemId: 'A4',
  templateId: 'policies-procedures',
  title: 'Written Policies and Procedures',
  description: 'Agency must maintain comprehensive written policies and procedures',
  severity: 'medium',

  questions: [
    {
      id: 'q1',
      question: 'Which of the following policies and procedures does your agency have documented in writing? (Select all that apply)',
      inputType: 'checklist',
      options: [
        { value: 'client_rights', label: 'Client rights and responsibilities' },
        { value: 'admission', label: 'Client admission, transfer, and discharge criteria' },
        { value: 'care_plan', label: 'Plan of care development and review' },
        { value: 'personnel', label: 'Personnel qualifications and job descriptions' },
        { value: 'training', label: 'Orientation and ongoing training requirements' },
        { value: 'supervision', label: 'Supervision of personnel' },
        { value: 'incident', label: 'Incident and accident reporting' },
        { value: 'complaint', label: 'Complaint and grievance procedures' },
        { value: 'infection', label: 'Infection control procedures' },
        { value: 'emergency', label: 'Emergency and disaster preparedness' },
        { value: 'qa', label: 'Quality assurance program' },
        { value: 'records', label: 'Record retention and confidentiality' },
      ],
      required: true,
      helpText: 'Select all policies your agency has documented',
    },
  ],

  scoringRules: [
    { condition: 'countAtLeast', value: 12, result: 'COMPLIANT' },
    { condition: 'countAtLeast', value: 9, result: 'AT_RISK' },
    { condition: 'countLessThan', value: 9, result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Your agency has documented policies and procedures covering all required operational areas per {state} regulations.',
    atRisk: 'Your policy manual is missing required sections. {agencyAbbr} surveyors will review your policies for completeness. Missing sections: {missing}. Develop these policies before your next survey.',
    nonCompliant: 'Your agency is missing multiple required policies per {state} regulations. This is a common survey deficiency. Immediately develop written policies for: {missing}. Consider engaging a compliance consultant to ensure completeness.',
    unknown: 'Review your policies and procedures manual against {state} requirements.',
  },
};

export const TEMPLATE_A5_SERVICE_AGREEMENTS: QuestionTemplate = {
  itemId: 'A5',
  templateId: 'service-agreements',
  title: 'Client Service Agreements',
  description: 'Written service agreements required for all clients',
  severity: 'medium',

  questions: [
    {
      id: 'q1',
      question: 'Do all your current clients have a signed service agreement on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all clients' },
        { value: 'most', label: 'Most clients, but some are missing' },
        { value: 'no', label: 'No / Not sure' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Which of the following does your service agreement template include? (Select all that apply)',
      inputType: 'checklist',
      options: [
        { value: 'services', label: 'Services to be provided' },
        { value: 'frequency', label: 'Frequency and duration of services' },
        { value: 'fees', label: 'Fees and payment terms' },
        { value: 'rights', label: 'Client rights and responsibilities' },
        { value: 'termination', label: 'Discharge and termination conditions' },
        { value: 'complaint', label: 'Complaint/grievance process' },
        { value: 'signatures', label: 'Signatures of client (or representative) and agency' },
        { value: 'date', label: 'Date of agreement' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'all' },
      { condition: 'countAtLeast', questionId: 'q2', value: 8 },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'most' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6 },
    ], result: 'AT_RISK' },
    { condition: 'countLessThan', questionId: 'q2', value: 6, result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Your client service agreements are complete and contain all required elements per {state} regulations.',
    atRisk: 'Some service agreements may be incomplete or missing. Agreements must be in place before services begin. Review all client files and ensure agreements include: {missing}.',
    nonCompliant: 'Multiple clients lack proper service agreements. This is a deficiency under {state} regulations. Immediately audit all client files, update your agreement template, and obtain signatures for all active clients.',
    unknown: 'Review client files to verify all service agreements are complete and signed.',
  },
};

export const TEMPLATE_A6_INCIDENT_REPORTING: QuestionTemplate = {
  itemId: 'A6',
  templateId: 'incident-reporting',
  title: 'Incident Reporting Process',
  description: 'Critical incidents must be reported to the state agency',
  severity: 'high',

  questions: [
    {
      id: 'q1',
      question: 'Does your agency have a written incident reporting policy?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unsure', label: 'Not sure' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Does your incident reporting process include: (Select all that apply)',
      inputType: 'checklist',
      options: [
        { value: 'definition', label: 'Definition of what constitutes a reportable incident' },
        { value: 'reporting_24hr', label: '24-hour reporting to {agencyAbbr} for serious incidents (death, serious injury, abuse)' },
        { value: 'documentation', label: 'Internal documentation within 48 hours of any incident' },
        { value: 'investigation', label: 'Investigation and root cause analysis' },
        { value: 'corrective', label: 'Corrective action documentation' },
        { value: 'training', label: 'Staff training on reporting procedures' },
      ],
      showCondition: {
        questionId: 'q1',
        operator: 'equals',
        value: 'yes',
      },
    },
    {
      id: 'q3',
      question: 'In the past 12 months, has your agency experienced any incidents that required reporting to {agencyAbbr}?',
      inputType: 'select',
      options: [
        { value: 'none', label: 'No incidents occurred' },
        { value: 'all_reported', label: 'Yes, and all were reported within 24 hours' },
        { value: 'some_late', label: 'Yes, but some were reported late' },
        { value: 'not_reported', label: 'Yes, and some were not reported' },
        { value: 'unsure', label: 'Not sure' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    { condition: 'equals', questionId: 'q3', value: 'not_reported', result: 'NON_COMPLIANT' },
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'yes' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      { condition: 'includes', questionId: 'q3', value: ['none', 'all_reported'] },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'countLessThan', questionId: 'q2', value: 6 },
      { condition: 'equals', questionId: 'q3', value: 'some_late' },
    ], result: 'AT_RISK' },
  ],

  reportLanguage: {
    compliant: 'Your incident reporting process meets {state} requirements.',
    atRisk: 'Your incident reporting process has gaps. Serious incidents require {agencyAbbr} notification within 24 hours. Update your policy to include: {missing}. Train all staff on the updated process.',
    nonCompliant: 'Failure to report incidents to {agencyAbbr} is a serious violation. This can result in immediate sanctions. Develop a written incident reporting policy immediately, train all staff, and ensure future incidents are reported within 24 hours.',
    unknown: 'Review your incident reporting policy against {state} requirements.',
  },
};

export const TEMPLATE_A10_RECORD_RETENTION: QuestionTemplate = {
  itemId: 'A10',
  templateId: 'record-retention',
  title: 'Record Retention Policy',
  description: 'Client records must be retained for the state-required minimum period',
  severity: 'medium',

  questions: [
    {
      id: 'q1',
      question: 'How long does your agency retain client records after discharge?',
      inputType: 'select',
      options: [
        { value: 'less_3', label: 'Less than 3 years' },
        { value: '3_5', label: '3-5 years' },
        { value: '6_plus', label: '6 years or more' },
        { value: '7_plus', label: '7 years or more' },
        { value: 'client_request', label: 'Until the client requests deletion' },
        { value: 'unsure', label: 'Not sure / no formal policy' },
      ],
      required: true,
      helpText: '{state} requires minimum {retentionYears} years retention after discharge',
    },
    {
      id: 'q2',
      question: 'Do you serve any clients who are minors (under 18)?',
      inputType: 'binary',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      id: 'q3',
      question: 'Are minor client records retained until at least 3 years after they turn 18 (age 21)?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unsure', label: 'Not sure' },
      ],
      showCondition: {
        questionId: 'q2',
        operator: 'equals',
        value: 'yes',
      },
    },
  ],

  scoringRules: [
    { condition: 'includes', questionId: 'q1', value: ['client_request', 'less_3'], result: 'NON_COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: '3_5', result: 'AT_RISK' },
    { condition: 'includes', questionId: 'q1', value: ['6_plus', '7_plus'], result: 'COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Your record retention policy meets {state}\'s {retentionYears}-year requirement.',
    atRisk: '{state} requires client records to be retained for at least {retentionYears} years after discharge. Review your retention policy and extend if needed.',
    nonCompliant: 'Your record retention period does not meet {state} requirements. Records must be kept for {retentionYears} years minimum. Update your policy immediately. Do not destroy any additional records until your policy is corrected.',
    unknown: 'Review your record retention policy against {state} requirements ({retentionYears} years minimum).',
  },

  // State-specific overrides for different retention periods
  stateOverrides: {
    CA: {
      scoringRules: [
        { condition: 'includes', questionId: 'q1', value: ['client_request', 'less_3', '3_5', '6_plus'], result: 'NON_COMPLIANT' },
        { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'AT_RISK' },
        { condition: 'equals', questionId: 'q1', value: '7_plus', result: 'COMPLIANT' },
      ],
    },
  },
};

export const TEMPLATE_A12_INSURANCE: QuestionTemplate = {
  itemId: 'A12',
  templateId: 'insurance',
  title: 'Liability Insurance',
  description: 'Agency must maintain required liability insurance coverage',
  severity: 'high',

  questions: [
    {
      id: 'q1',
      question: 'Does your agency currently have general liability insurance?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unsure', label: 'Not sure' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'What is your coverage amount?',
      inputType: 'select',
      options: [
        { value: 'less_500k', label: 'Less than $500,000' },
        { value: '500k_999k', label: '$500,000 - $999,999' },
        { value: '1m_plus', label: '$1,000,000 or more' },
        { value: '1m_3m', label: '$1M per occurrence AND $3M+ aggregate' },
        { value: 'unsure', label: 'Not sure' },
      ],
      showCondition: {
        questionId: 'q1',
        operator: 'equals',
        value: 'yes',
      },
    },
    {
      id: 'q3',
      question: 'When does your current policy expire?',
      inputType: 'date',
      showCondition: {
        questionId: 'q1',
        operator: 'equals',
        value: 'yes',
      },
    },
  ],

  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    { condition: 'equals', questionId: 'q2', value: 'less_500k', result: 'NON_COMPLIANT' },
    { condition: 'allTrue', value: [
      { condition: 'includes', questionId: 'q2', value: ['1m_plus', '1m_3m'] },
      { condition: 'daysFuture', questionId: 'q3', value: 30 },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q2', value: '500k_999k' },
      { condition: 'daysFuture', questionId: 'q3', value: 0 },
    ], result: 'AT_RISK' },
  ],

  reportLanguage: {
    compliant: 'Your general liability insurance meets {state}\'s {insuranceMinimum} minimum requirement.',
    atRisk: 'Your insurance coverage may not meet {state}\'s {insuranceMinimum} minimum OR your policy is expiring soon. Contact your insurance provider to verify coverage and renew before expiration.',
    nonCompliant: 'Operating without adequate liability insurance is a licensure violation. You must maintain at least {insuranceMinimum} in general liability coverage. Obtain proper insurance immediately.',
    unknown: 'Verify your insurance coverage meets {state} requirements ({insuranceMinimum} minimum).',
  },

  // California has more complex insurance requirements
  stateOverrides: {
    CA: {
      questions: [
        {
          id: 'q1',
          question: 'Does your agency have general and professional liability insurance?',
          inputType: 'select',
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'unsure', label: 'Not sure' },
          ],
          required: true,
        },
        {
          id: 'q2',
          question: 'What is your coverage amount?',
          inputType: 'select',
          options: [
            { value: 'less_1m', label: 'Less than $1M per occurrence' },
            { value: '1m_less_3m', label: '$1M+ per occurrence, but less than $3M aggregate' },
            { value: '1m_3m', label: '$1M+ per occurrence AND $3M+ aggregate' },
            { value: 'unsure', label: 'Not sure' },
          ],
          showCondition: {
            questionId: 'q1',
            operator: 'equals',
            value: 'yes',
          },
        },
        {
          id: 'q3',
          question: 'Do you have a current workers\' compensation policy covering all Home Care Aides?',
          inputType: 'select',
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no_employees', label: 'No — we have no employees (owner only)' },
            { value: 'no', label: 'No' },
            { value: 'unsure', label: 'Not sure' },
          ],
          required: true,
        },
        {
          id: 'q4',
          question: 'Do you have an employee dishonesty bond with a minimum limit of $10,000?',
          inputType: 'select',
          options: [
            { value: 'yes_10k', label: 'Yes, $10,000 or more' },
            { value: 'yes_less', label: 'Yes, but less than $10,000' },
            { value: 'no', label: 'No' },
            { value: 'unsure', label: 'Not sure what this is' },
          ],
          required: true,
        },
      ],
      reportLanguage: {
        compliant: 'Your insurance coverage meets all California requirements under HSC 1796.37.',
        atRisk: 'California requires THREE types of coverage: (1) Liability - $1M per occurrence/$3M aggregate, (2) Workers\' Compensation for all HCAs, (3) Employee Dishonesty Bond - $10K minimum. You are missing or under-covered on: {missing}. Contact your insurance broker to add coverage.',
        nonCompliant: 'You are missing required insurance under HSC 1796.37. CDSS can impose fines of $900-$5,000 per violation. Obtain proper coverage immediately.',
        unknown: 'Verify your insurance coverage meets California HSC 1796.37 requirements.',
      },
    },
  },
};

// ============================================
// SECTION B: PERSONNEL FILES
// ============================================

export const TEMPLATE_B1_PERSONNEL_FILES: QuestionTemplate = {
  itemId: 'B1',
  templateId: 'personnel-files',
  title: 'Personnel File Completeness',
  description: 'Personnel files must contain all required documents',
  severity: 'high',

  questions: [
    {
      id: 'q1',
      question: 'Which documents are consistently maintained in every caregiver personnel file? (Select all that apply)',
      inputType: 'checklist',
      options: [
        { value: 'photo_id', label: 'Government-issued photo ID' },
        { value: 'job_description', label: 'Signed job description' },
        { value: 'hipaa', label: 'HIPAA/Confidentiality agreement' },
        { value: 'background_check', label: 'Background check results' },
        { value: 'i9', label: 'I-9 Employment Verification' },
        { value: 'training_records', label: 'Training completion records' },
        { value: 'health_clearance', label: 'Health clearance/TB test' },
        { value: 'emergency_contact', label: 'Emergency contact information' },
      ],
      required: true,
      helpText: 'Select all documents that are in 100% of your caregiver files',
    },
  ],

  scoringRules: [
    { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
    { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
    { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Personnel files contain all required documents per {state} regulations.',
    atRisk: 'Personnel files are missing some required documents. Audit all files and add missing items: {missing}.',
    nonCompliant: 'Personnel files are significantly incomplete. {agencyAbbr} surveyors will cite missing documentation as deficiencies. Missing items: {missing}.',
    unknown: 'Conduct a full personnel file audit against {state} requirements.',
  },
};

// ============================================
// SECTION C: BACKGROUND SCREENING
// ============================================

export const TEMPLATE_C1_BACKGROUND_CHECK: QuestionTemplate = {
  itemId: 'C1',
  templateId: 'background-check',
  title: '{state} Criminal Background Check Compliance',
  description: 'All caregivers must have completed {backgroundCheckType} background checks before providing services',
  severity: 'high',

  questions: [
    {
      id: 'q1',
      question: 'Are {backgroundCheckType} criminal background checks completed before caregivers begin working with clients?',
      inputType: 'select',
      options: [
        { value: 'always_before', label: 'Always - before first shift' },
        { value: 'usually_before', label: 'Usually - within first week' },
        { value: 'after_start', label: 'Sometimes after they start' },
        { value: 'not_always', label: 'Not consistently done' },
        { value: 'unsure', label: 'Not sure' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'What type of background check do you conduct? {state} requires: {registryChecks}',
      inputType: 'checklist',
      options: [
        { value: 'state', label: 'State criminal background check' },
        { value: 'fbi', label: 'FBI fingerprint check' },
        { value: 'oig', label: 'OIG/GSA exclusion list check' },
        { value: 'registry', label: 'State abuse/neglect registry check' },
        { value: 'sex_offender', label: 'Sex offender registry check' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'always_before', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'usually_before', result: 'AT_RISK' },
    { condition: 'includes', questionId: 'q1', value: ['after_start', 'not_always'], result: 'NON_COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'UNKNOWN' },
  ],

  reportLanguage: {
    compliant: '{state} requires {backgroundCheckType} background checks. Your agency completes these before employment, meeting {agencyAbbr} requirements.',
    atRisk: 'Background checks are not consistently completed before first shift. {state} requires {backgroundCheckType} checks to be completed prior to employment. {agencyAbbr} rescreening is required {backgroundFrequencyText}. Tighten your onboarding process.',
    nonCompliant: 'Allowing caregivers to work before background check completion violates {state} regulations. {state} requires {backgroundCheckType} background checks through {registryChecks}. This is a high-severity deficiency that {agencyAbbr} surveyors will cite. Contact {phoneNumber} for guidance.',
    unknown: 'Verify your background check process meets {state} requirements. {agencyAbbr} requires {backgroundCheckType} checks {backgroundFrequencyText}.',
  },

  // California requires Live Scan (DOJ + FBI)
  stateOverrides: {
    CA: {
      questions: [
        {
          id: 'q1',
          question: 'For ALL currently employed Home Care Aides, which of the following do you have on file? (Select all that apply)',
          inputType: 'checklist',
          options: [
            { value: 'livescan', label: 'Live Scan receipt/confirmation for each HCA' },
            { value: 'doj', label: 'DOJ clearance or exemption letter for each HCA' },
            { value: 'fbi', label: 'FBI clearance confirmation for each HCA' },
            { value: 'lic508', label: 'Criminal Record Statement (LIC 508) signed by each HCA' },
            { value: 'exemptions', label: 'Documentation of any exemptions granted by CBCB' },
            { value: 'unsure', label: 'Not sure / records incomplete' },
          ],
          required: true,
        },
      ],
      scoringRules: [
        { condition: 'includes', value: 'unsure', result: 'AT_RISK' },
        { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
        { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
        { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
      ],
      reportLanguage: {
        compliant: 'Background check documentation is complete for all HCAs per HSC 1796.22.',
        atRisk: 'Some background check documentation may be incomplete. California requires BOTH DOJ and FBI checks via Live Scan. Ensure each HCA file contains: Live Scan confirmation, DOJ clearance, FBI clearance, and signed LIC 508.',
        nonCompliant: 'Employing individuals without proper background clearance violates HSC 1796.22 and can result in license revocation. Immediately verify clearance status for all HCAs.',
        unknown: 'Audit your background check documentation against HSC 1796.22 requirements.',
      },
    },
  },
};

// ============================================
// SECTION E: TRAINING & COMPETENCY
// ============================================

export const TEMPLATE_E3_TRAINING_HOURS: QuestionTemplate = {
  itemId: 'E3',
  templateId: 'training-hours',
  title: 'Annual Training Hours',
  description: 'Caregivers must complete required annual training hours',
  severity: 'high',

  questions: [
    {
      id: 'q1',
      question: 'How many in-service training hours do your caregivers complete annually?',
      inputType: 'number',
      placeholder: 'Enter hours per year',
      unit: 'hours/year',
      helpText: '{state} requires minimum {annualTrainingHours} hours of annual in-service training',
      required: true,
    },
    {
      id: 'q2',
      question: 'Do you maintain training records (certificates, sign-in sheets, completion dates) for each caregiver?',
      inputType: 'select',
      options: [
        { value: 'yes_all', label: 'Yes, for all caregivers' },
        { value: 'yes_most', label: 'For most caregivers' },
        { value: 'no', label: 'No / incomplete records' },
        { value: 'unsure', label: 'Not sure' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'greaterOrEqual', questionId: 'q1', value: 12, result: 'COMPLIANT' },
    { condition: 'greaterOrEqual', questionId: 'q1', value: 8, result: 'AT_RISK' },
    { condition: 'lessThan', questionId: 'q1', value: 8, result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Your training program meets {state}\'s {annualTrainingHours}-hour annual requirement.',
    atRisk: 'Your training hours are below {state}\'s {annualTrainingHours}-hour annual requirement. Increase training to maintain compliance.',
    nonCompliant: '{state} requires {annualTrainingHours} hours of annual in-service training. Your current program is significantly below this requirement.',
    unknown: 'Audit your training records against {state} requirements ({annualTrainingHours} hours annually).',
  },

  // California has specific 5-hour requirement with prescribed topics
  stateOverrides: {
    CA: {
      questions: [
        {
          id: 'q1',
          question: 'For entry-level training, do ALL new HCAs complete the following BEFORE any client contact?',
          inputType: 'checklist',
          options: [
            { value: 'orientation', label: '2 hours orientation (role as caregiver, terms of employment)' },
            { value: 'safety', label: '3 hours safety training (basic safety, emergency procedures, infection control)' },
            { value: 'documentation', label: 'Training documentation signed and dated' },
          ],
          required: true,
        },
        {
          id: 'q2',
          question: 'For annual training, do ALL HCAs complete 5 hours of continuing education each year covering:',
          inputType: 'checklist',
          options: [
            { value: 'rights', label: 'Client rights and safety' },
            { value: 'daily_living', label: 'Daily living needs assistance' },
            { value: 'hygiene', label: 'Personal hygiene assistance' },
            { value: 'transport', label: 'Safe transportation (if applicable)' },
            { value: 'population', label: 'Population-specific topics' },
          ],
          required: true,
        },
        {
          id: 'q3',
          question: 'Do you maintain training records for each HCA?',
          inputType: 'select',
          options: [
            { value: 'yes_all', label: 'Yes, for all HCAs' },
            { value: 'yes_most', label: 'For most HCAs' },
            { value: 'no', label: 'No / incomplete records' },
            { value: 'unsure', label: 'Not sure' },
          ],
          required: true,
        },
      ],
      scoringRules: [
        { condition: 'allTrue', value: [
          { condition: 'countAtLeast', questionId: 'q1', value: 3 },
          { condition: 'countAtLeast', questionId: 'q2', value: 4 },
          { condition: 'equals', questionId: 'q3', value: 'yes_all' },
        ], result: 'COMPLIANT' },
        { condition: 'anyTrue', value: [
          { condition: 'countAtLeast', questionId: 'q1', value: 2 },
          { condition: 'equals', questionId: 'q3', value: 'yes_most' },
        ], result: 'AT_RISK' },
      ],
      reportLanguage: {
        compliant: 'Your training program meets California requirements under HSC 1796.44.',
        atRisk: 'Training documentation is incomplete or some HCAs are overdue. Per HSC 1796.44, training can be online but must be documented. Ensure each HCA file contains proof of: 5 hours entry-level (before first client) and 5 hours annual training.',
        nonCompliant: 'HCAs are providing services without required training. This violates HSC 1796.44. Immediately complete entry-level training for any HCA who started without it. Training must include: 2 hours orientation + 3 hours safety BEFORE client contact.',
        unknown: 'Review your training program against HSC 1796.44 requirements.',
      },
    },
  },
};

// ============================================
// SECTION B: PERSONNEL FILES (B2-B10)
// ============================================

export const TEMPLATE_B2_PHOTO_ID: QuestionTemplate = {
  itemId: 'B2',
  templateId: 'photo-id',
  title: 'Government-Issued Photo ID',
  description: 'Valid photo ID required for all personnel',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL your caregivers have a valid, non-expired government-issued photo ID on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all caregivers' },
        { value: 'most', label: 'Most, but some are missing or expired' },
        { value: 'no', label: 'No / Not sure' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'All personnel have valid government-issued photo ID on file.',
    atRisk: 'Some personnel files are missing valid photo ID. Review all files and obtain copies of current IDs.',
    nonCompliant: 'Personnel files are missing required photo ID documentation. This is a basic compliance requirement.',
    unknown: 'Verify all personnel have valid photo ID on file.',
  },
};

export const TEMPLATE_B3_JOB_DESCRIPTION: QuestionTemplate = {
  itemId: 'B3',
  templateId: 'job-description',
  title: 'Signed Job Description',
  description: 'Job descriptions must outline duties and be signed by employee',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL caregivers have a signed job description on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have signed job descriptions' },
        { value: 'most', label: 'Most do, some are missing' },
        { value: 'no', label: 'No / We don\'t use job descriptions' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'All personnel have signed job descriptions on file.',
    atRisk: 'Some personnel are missing signed job descriptions. Obtain signatures from all staff.',
    nonCompliant: 'Job descriptions are required documentation. Create and have all staff sign their job descriptions.',
    unknown: 'Verify all personnel have signed job descriptions.',
  },
};

export const TEMPLATE_B4_HIPAA: QuestionTemplate = {
  itemId: 'B4',
  templateId: 'hipaa-agreement',
  title: 'HIPAA/Confidentiality Agreement',
  description: 'Employees must acknowledge privacy obligations',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL caregivers have a signed HIPAA/confidentiality agreement on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have signed' },
        { value: 'most', label: 'Most have signed' },
        { value: 'no', label: 'No / We don\'t have this form' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'All personnel have signed HIPAA/confidentiality agreements.',
    atRisk: 'Some personnel are missing signed confidentiality agreements. This is a high-priority item.',
    nonCompliant: 'HIPAA training and confidentiality agreements are required. This exposes your agency to significant liability.',
    unknown: 'Verify all personnel have signed HIPAA agreements.',
  },
};

export const TEMPLATE_B5_CODE_OF_CONDUCT: QuestionTemplate = {
  itemId: 'B5',
  templateId: 'code-of-conduct',
  title: 'Code of Conduct/Ethics Agreement',
  description: 'Written acknowledgment of behavioral expectations',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL caregivers have a signed code of conduct/ethics agreement on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have signed' },
        { value: 'most', label: 'Most have signed' },
        { value: 'no', label: 'No / We don\'t have this' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'All personnel have signed code of conduct agreements.',
    atRisk: 'Some personnel are missing signed code of conduct. Obtain signatures from all staff.',
    nonCompliant: 'A code of conduct establishes behavioral expectations. Create this document and have all staff sign.',
    unknown: 'Verify all personnel have signed code of conduct.',
  },
};

export const TEMPLATE_B6_ATTENDANCE: QuestionTemplate = {
  itemId: 'B6',
  templateId: 'attendance-policy',
  title: 'Attendance Policy Acknowledgment',
  description: 'Employee understands attendance requirements',
  severity: 'low',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL caregivers have a signed attendance policy acknowledgment on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have signed' },
        { value: 'most', label: 'Most have signed' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'All personnel have acknowledged the attendance policy.',
    atRisk: 'Some personnel are missing attendance policy acknowledgment.',
    nonCompliant: 'Attendance policy acknowledgment helps document expectations with employees.',
    unknown: 'Verify all personnel have acknowledged attendance policy.',
  },
};

export const TEMPLATE_B7_TRAINING_POLICY: QuestionTemplate = {
  itemId: 'B7',
  templateId: 'training-policy-ack',
  title: 'Training Policy Acknowledgment',
  description: 'Employee understands ongoing training requirements',
  severity: 'low',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL caregivers have a signed training policy acknowledgment on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have signed' },
        { value: 'most', label: 'Most have signed' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'All personnel have acknowledged the training policy.',
    atRisk: 'Some personnel are missing training policy acknowledgment.',
    nonCompliant: 'Training policy acknowledgment documents that employees understand requirements.',
    unknown: 'Verify all personnel have acknowledged training policy.',
  },
};

export const TEMPLATE_B8_BG_CONSENT: QuestionTemplate = {
  itemId: 'B8',
  templateId: 'background-consent',
  title: 'Background Check Consent Form',
  description: 'Written authorization for background screening',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL caregivers have a signed background check consent form on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have signed consent' },
        { value: 'most', label: 'Most have signed' },
        { value: 'no', label: 'No / Not sure' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'All personnel have signed background check consent forms.',
    atRisk: 'Some personnel are missing background check consent. This is required before running checks.',
    nonCompliant: 'Background check consent is legally required before screening. Obtain consent from all staff immediately.',
    unknown: 'Verify all personnel have signed background check consent.',
  },
};

export const TEMPLATE_B9_I9: QuestionTemplate = {
  itemId: 'B9',
  templateId: 'i9-verification',
  title: 'I-9 Employment Eligibility Verification',
  description: 'Federal requirement for all employees',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Are I-9 forms completed for ALL employees within 3 days of hire?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all I-9s completed on time' },
        { value: 'most', label: 'Most are completed, some may be late' },
        { value: 'no', label: 'No / Not sure' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Are I-9 forms stored separately from personnel files as recommended?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes, stored separately' },
        { value: 'no', label: 'No, kept in personnel files' },
        { value: 'unsure', label: 'Not sure' },
      ],
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'I-9 verification is properly completed for all employees.',
    atRisk: 'Some I-9 forms may be incomplete or late. Review all forms for compliance with federal requirements.',
    nonCompliant: 'I-9 compliance is a federal requirement. Failure to comply can result in significant fines. Audit all employee I-9s immediately.',
    unknown: 'Verify I-9 completion status for all employees.',
  },
};

export const TEMPLATE_B10_TAX_FORMS: QuestionTemplate = {
  itemId: 'B10',
  templateId: 'tax-forms',
  title: 'Tax Onboarding Forms',
  description: 'W-4 and state withholding forms required for payroll',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Are W-4 and state tax withholding forms completed for ALL employees?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all forms completed' },
        { value: 'most', label: 'Most are completed' },
        { value: 'no', label: 'No / Not sure' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Tax withholding forms are completed for all employees.',
    atRisk: 'Some employees may be missing tax forms. Review payroll records.',
    nonCompliant: 'Tax withholding forms are required for proper payroll processing.',
    unknown: 'Verify tax form completion for all employees.',
  },
};

// ============================================
// SECTION C: BACKGROUND SCREENING (C2-C7)
// ============================================

export const TEMPLATE_C2_BG_RESULTS: QuestionTemplate = {
  itemId: 'C2',
  templateId: 'bg-results-documented',
  title: 'Background Check Results Documented',
  description: 'Results or verification letter must be on file',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do you have documented background check results or clearance letters on file for ALL caregivers?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have documented results' },
        { value: 'most', label: 'Most have results, some missing' },
        { value: 'no', label: 'No / Results not kept on file' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Background check results are properly documented for all personnel.',
    atRisk: 'Some personnel files are missing background check documentation. Obtain copies of results.',
    nonCompliant: 'Background check results must be documented and retained. This is critical for survey readiness.',
    unknown: 'Verify background check documentation for all personnel.',
  },
};

export const TEMPLATE_C3_OIG_GSA: QuestionTemplate = {
  itemId: 'C3',
  templateId: 'oig-gsa-exclusion',
  title: 'OIG/GSA Exclusion List Checks',
  description: 'Verify employees are not excluded from federal programs',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do you check the OIG exclusion list for ALL employees before hire?',
      inputType: 'select',
      options: [
        { value: 'always', label: 'Yes, always before hire' },
        { value: 'sometimes', label: 'Sometimes' },
        { value: 'no', label: 'No / Not familiar with this' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Do you perform monthly OIG exclusion checks on current employees?',
      inputType: 'select',
      options: [
        { value: 'monthly', label: 'Yes, monthly' },
        { value: 'quarterly', label: 'Quarterly' },
        { value: 'annually', label: 'Annually' },
        { value: 'no', label: 'No ongoing checks' },
      ],
    },
  ],
  scoringRules: [
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'always' },
      { condition: 'equals', questionId: 'q2', value: 'monthly' },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'sometimes' },
      { condition: 'includes', questionId: 'q2', value: ['quarterly', 'annually'] },
    ], result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'OIG/GSA exclusion checks are performed properly.',
    atRisk: 'OIG checks should be performed before hire and monthly thereafter. Increase check frequency.',
    nonCompliant: 'OIG exclusion checks are required for Medicaid/Medicare billing. Employing excluded individuals can result in severe penalties.',
    unknown: 'Implement OIG exclusion checking process.',
  },
};

export const TEMPLATE_C4_RESCREENING: QuestionTemplate = {
  itemId: 'C4',
  templateId: 'rescreening-policy',
  title: '{state} Background Check Re-screening',
  description: '{state} requires background check renewal {backgroundFrequencyText}',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do you have a policy for re-screening employees {backgroundFrequencyText} per {state} requirements?',
      inputType: 'select',
      options: [
        { value: 'yes_tracked', label: 'Yes, and we track expiration dates' },
        { value: 'yes_not_tracked', label: 'Yes, but don\'t actively track' },
        { value: 'no', label: 'No re-screening policy' },
      ],
      required: true,
      helpText: '{state} requires {backgroundCheckType} background checks to be renewed {backgroundFrequencyText}',
    },
    {
      id: 'q2',
      question: 'Are all current employees\' background checks within the {backgroundFrequency}-year renewal period?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all are current' },
        { value: 'most', label: 'Most are current, some overdue' },
        { value: 'unsure', label: 'Not sure' },
      ],
    },
  ],
  scoringRules: [
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'yes_tracked' },
      { condition: 'equals', questionId: 'q2', value: 'all' },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'yes_not_tracked' },
      { condition: 'equals', questionId: 'q2', value: 'most' },
    ], result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: '{state} re-screening policy is in place. All employee background checks are current per {agencyAbbr} requirements ({backgroundFrequencyText}).',
    atRisk: 'Re-screening tracking needs improvement. {state} requires {backgroundCheckType} background checks {backgroundFrequencyText}. Some employees may have expired background checks. Review all personnel files for compliance.',
    nonCompliant: '{state} requires background check renewal {backgroundFrequencyText}. Operating without a re-screening policy violates {agencyAbbr} regulations. Contact {phoneNumber} for guidance. Implement tracking immediately.',
    unknown: 'Review re-screening requirements for {state}. {agencyAbbr} requires renewal {backgroundFrequencyText}.',
  },
};

export const TEMPLATE_C7_DRIVING: QuestionTemplate = {
  itemId: 'C7',
  templateId: 'driving-record',
  title: 'Driving Record Check',
  description: 'Required for staff who transport clients',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do any of your caregivers transport clients as part of their duties?',
      inputType: 'binary',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Do you perform driving record (MVR) checks for staff who transport clients?',
      inputType: 'select',
      options: [
        { value: 'always', label: 'Yes, always before allowing transport' },
        { value: 'sometimes', label: 'Sometimes' },
        { value: 'no', label: 'No' },
      ],
      showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'yes' },
      { condition: 'equals', questionId: 'q2', value: 'always' },
    ], result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q2', value: 'sometimes', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Driving record checks are properly performed for transport staff.',
    atRisk: 'Driving checks should be performed for ALL staff who transport clients.',
    nonCompliant: 'Staff transporting clients must have MVR checks. This is a liability concern.',
    unknown: 'Verify driving record check policy.',
  },
};

// ============================================
// SECTION D: HEALTH & SAFETY (D1-D7)
// ============================================

export const TEMPLATE_D1_TB: QuestionTemplate = {
  itemId: 'D1',
  templateId: 'tb-screening',
  title: '{state} TB Screening Requirements',
  description: 'Tuberculosis screening for all direct care staff per {agencyAbbr} requirements',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL direct care staff have current TB screening on file per {state} requirements?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have current TB clearance' },
        { value: 'most', label: 'Most do, some missing or expired' },
        { value: 'no', label: 'No / Not sure' },
      ],
      required: true,
      helpText: '{state} requires TB clearance before direct client contact',
    },
    {
      id: 'q2',
      question: 'How do you track TB screening expiration?',
      inputType: 'select',
      options: [
        { value: 'system', label: 'Automated system with alerts' },
        { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
        { value: 'none', label: 'No active tracking' },
      ],
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'TB screening documentation is current for all direct care staff per {state} requirements.',
    atRisk: 'Some TB screenings may be missing or expired. {state} requires TB clearance before direct client contact and periodic rescreening. Review all personnel files and schedule testing for anyone whose clearance is expired.',
    nonCompliant: '{state} requires TB screening for all direct care staff before providing services. Staff are working without valid TB clearance, creating health risks and regulatory exposure. Contact {agencyAbbr} at {phoneNumber} for guidance. Immediately schedule TB testing.',
    unknown: 'Verify TB screening status for all direct care staff per {state} requirements.',
  },
};

export const TEMPLATE_D2_HEALTH_CLEARANCE: QuestionTemplate = {
  itemId: 'D2',
  templateId: 'health-clearance',
  title: 'Health Clearance/Physical Exam',
  description: 'Fitness for duty assessment',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL caregivers have health clearance or physical exam documentation on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have documentation' },
        { value: 'most', label: 'Most do, some missing' },
        { value: 'no', label: 'No / We don\'t require this' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Health clearance documentation is on file for all personnel.',
    atRisk: 'Some personnel are missing health clearance. Obtain documentation.',
    nonCompliant: 'Health clearance ensures caregivers are fit for duty.',
    unknown: 'Verify health clearance documentation.',
  },
};

export const TEMPLATE_D3_CPR: QuestionTemplate = {
  itemId: 'D3',
  templateId: 'cpr-certification',
  title: 'CPR Certification',
  description: 'Current CPR certification from approved provider',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL direct care staff have current CPR certification?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have current certification' },
        { value: 'most', label: 'Most do, some expired or missing' },
        { value: 'no', label: 'No / Not required for our services' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'CPR certification is current for all direct care staff.',
    atRisk: 'Some CPR certifications are expired or missing. Schedule recertification.',
    nonCompliant: 'CPR certification is typically required for direct care staff. Verify state requirements.',
    unknown: 'Verify CPR certification status.',
  },
};

export const TEMPLATE_D4_FIRST_AID: QuestionTemplate = {
  itemId: 'D4',
  templateId: 'first-aid',
  title: 'First Aid Certification',
  description: 'Current First Aid certification',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL direct care staff have current First Aid certification?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have current certification' },
        { value: 'most', label: 'Most do, some expired or missing' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'First Aid certification is current for all direct care staff.',
    atRisk: 'Some First Aid certifications are expired or missing.',
    nonCompliant: 'First Aid certification is required. Schedule training immediately.',
    unknown: 'Verify First Aid certification status.',
  },
};

export const TEMPLATE_D5_INFECTION_CONTROL: QuestionTemplate = {
  itemId: 'D5',
  templateId: 'infection-control',
  title: 'Infection Control Training',
  description: 'Training on infection prevention procedures',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Have ALL caregivers completed infection control training?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have completed' },
        { value: 'most', label: 'Most have completed' },
        { value: 'no', label: 'No / Training not provided' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Infection control training is complete for all caregivers.',
    atRisk: 'Some caregivers need infection control training.',
    nonCompliant: 'Infection control training is essential. Implement training program.',
    unknown: 'Verify infection control training status.',
  },
};

export const TEMPLATE_D6_BLOODBORNE: QuestionTemplate = {
  itemId: 'D6',
  templateId: 'bloodborne-pathogens',
  title: 'Bloodborne Pathogens Training',
  description: 'OSHA required training for healthcare workers',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Have ALL caregivers completed bloodborne pathogens training?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have completed' },
        { value: 'most', label: 'Most have completed' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Bloodborne pathogens training is complete for all caregivers.',
    atRisk: 'Some caregivers need bloodborne pathogens training.',
    nonCompliant: 'Bloodborne pathogens training is OSHA required. Implement immediately.',
    unknown: 'Verify bloodborne pathogens training status.',
  },
};

export const TEMPLATE_D7_PPE: QuestionTemplate = {
  itemId: 'D7',
  templateId: 'ppe-policy',
  title: 'PPE Policy Acknowledgment',
  description: 'Employee understands proper use of PPE',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Have ALL caregivers acknowledged your PPE policy?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have acknowledged' },
        { value: 'most', label: 'Most have acknowledged' },
        { value: 'no', label: 'No / We don\'t have a PPE policy' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'PPE policy is acknowledged by all caregivers.',
    atRisk: 'Some caregivers need to acknowledge PPE policy.',
    nonCompliant: 'A PPE policy is important for staff safety. Create and implement.',
    unknown: 'Verify PPE policy acknowledgment status.',
  },
};

// ============================================
// SECTION E: TRAINING & COMPETENCY (E1-E8)
// ============================================

export const TEMPLATE_E1_ORIENTATION: QuestionTemplate = {
  itemId: 'E1',
  templateId: 'orientation',
  title: 'New Hire Orientation',
  description: 'Comprehensive orientation before providing care',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do ALL caregivers complete orientation BEFORE providing care to clients?',
      inputType: 'select',
      options: [
        { value: 'always', label: 'Yes, always before first client' },
        { value: 'usually', label: 'Usually, but sometimes start early' },
        { value: 'no', label: 'No structured orientation program' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'How many hours is your orientation program?',
      inputType: 'select',
      options: [
        { value: 'meets', label: '{entryLevelTrainingHours}+ hours (meets state requirement)' },
        { value: 'below', label: 'Less than {entryLevelTrainingHours} hours' },
        { value: 'unsure', label: 'Not sure' },
      ],
    },
  ],
  scoringRules: [
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'always' },
      { condition: 'equals', questionId: 'q2', value: 'meets' },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'usually' },
      { condition: 'equals', questionId: 'q2', value: 'below' },
    ], result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Orientation program meets {state} requirements.',
    atRisk: 'Orientation may not fully meet requirements. Review program against {state} standards.',
    nonCompliant: '{state} requires {entryLevelTrainingHours} hours of orientation before providing care.',
    unknown: 'Verify orientation program meets requirements.',
  },
};

export const TEMPLATE_E2_COMPETENCY: QuestionTemplate = {
  itemId: 'E2',
  templateId: 'competency-evaluation',
  title: 'Competency Evaluation',
  description: 'Skills assessment for direct care tasks',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do you perform competency evaluations for ALL caregivers?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all caregivers are evaluated' },
        { value: 'some', label: 'Some are evaluated' },
        { value: 'no', label: 'No formal competency evaluation' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Competency evaluations are performed for all caregivers.',
    atRisk: 'Competency evaluations should be performed for all caregivers.',
    nonCompliant: 'Competency evaluation verifies caregivers can perform required tasks safely.',
    unknown: 'Verify competency evaluation process.',
  },
};

export const TEMPLATE_E4_DEMENTIA: QuestionTemplate = {
  itemId: 'E4',
  templateId: 'dementia-training',
  title: 'Dementia/Alzheimer\'s Training',
  description: 'Required for staff serving clients with cognitive impairment',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do you serve clients with dementia or Alzheimer\'s?',
      inputType: 'binary',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Have caregivers serving these clients completed dementia training?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have completed' },
        { value: 'some', label: 'Some have completed' },
        { value: 'no', label: 'No' },
      ],
      showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'yes' },
      { condition: 'equals', questionId: 'q2', value: 'all' },
    ], result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q2', value: 'some', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Dementia training is complete for applicable staff.',
    atRisk: 'Some staff serving dementia clients need training.',
    nonCompliant: 'Staff serving dementia clients must have specialized training.',
    unknown: 'Verify dementia training requirements.',
  },
};

export const TEMPLATE_E5_TRANSFER: QuestionTemplate = {
  itemId: 'E5',
  templateId: 'transfer-training',
  title: 'Transfer and Mobility Training',
  description: 'Safe lifting and transfer techniques',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Have ALL caregivers who assist with transfers completed safe transfer training?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have completed' },
        { value: 'some', label: 'Some have completed' },
        { value: 'no', label: 'No formal training' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Transfer and mobility training is complete.',
    atRisk: 'Some caregivers need transfer training.',
    nonCompliant: 'Safe transfer training prevents injuries to clients and staff.',
    unknown: 'Verify transfer training status.',
  },
};

export const TEMPLATE_E6_MEDICATION: QuestionTemplate = {
  itemId: 'E6',
  templateId: 'medication-training',
  title: 'Medication Assistance Training',
  description: 'Required for staff assisting with medications',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do your caregivers assist clients with medications?',
      inputType: 'binary',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Have ALL caregivers who assist with medications completed medication training?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have completed' },
        { value: 'some', label: 'Some have completed' },
        { value: 'no', label: 'No' },
      ],
      showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'yes' },
      { condition: 'equals', questionId: 'q2', value: 'all' },
    ], result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q2', value: 'some', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Medication assistance training is complete.',
    atRisk: 'Some staff assisting with medications need training.',
    nonCompliant: 'Medication assistance training is required by {state} regulations.',
    unknown: 'Verify medication training requirements.',
  },
};

export const TEMPLATE_E7_ABUSE: QuestionTemplate = {
  itemId: 'E7',
  templateId: 'abuse-training',
  title: 'Abuse/Neglect Recognition Training',
  description: 'Mandatory reporter training',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Have ALL caregivers completed abuse/neglect recognition and reporting training?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have completed' },
        { value: 'some', label: 'Some have completed' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Abuse recognition training is complete for all caregivers.',
    atRisk: 'Some caregivers need abuse recognition training.',
    nonCompliant: 'Caregivers are mandatory reporters. Training is legally required.',
    unknown: 'Verify abuse recognition training status.',
  },
};

export const TEMPLATE_E8_CLIENT_RIGHTS: QuestionTemplate = {
  itemId: 'E8',
  templateId: 'client-rights',
  title: 'Client Rights Training',
  description: 'Understanding of client rights and dignity',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Have ALL caregivers completed client rights training?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have completed' },
        { value: 'some', label: 'Some have completed' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Client rights training is complete for all caregivers.',
    atRisk: 'Some caregivers need client rights training.',
    nonCompliant: 'Client rights training is a fundamental requirement.',
    unknown: 'Verify client rights training status.',
  },
};

// ============================================
// SECTION F: CARE DELIVERY (F1-F5)
// ============================================

export const TEMPLATE_F1_CARE_PLANS: QuestionTemplate = {
  itemId: 'F1',
  templateId: 'care-plans',
  title: 'Care Plans',
  description: 'Individualized care plans for each client',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Are care plans completed BEFORE caregivers start working with each client?',
      inputType: 'select',
      options: [
        { value: 'always', label: 'Yes, always before first shift' },
        { value: 'usually', label: 'Usually, but sometimes started after' },
        { value: 'no', label: 'No / We don\'t use formal care plans' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Care plans are completed before service delivery.',
    atRisk: 'Care plans should always be completed before first shift.',
    nonCompliant: 'Care plans are required before providing services.',
    unknown: 'Verify care plan process.',
  },
};

export const TEMPLATE_F2_CARE_PLAN_ACK: QuestionTemplate = {
  itemId: 'F2',
  templateId: 'care-plan-acknowledgment',
  title: 'Caregiver Care Plan Acknowledgment',
  description: 'Staff signs that they reviewed and understand care plan',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do caregivers sign acknowledgment that they have reviewed each client\'s care plan?',
      inputType: 'select',
      options: [
        { value: 'always', label: 'Yes, always signed' },
        { value: 'sometimes', label: 'Sometimes' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Caregivers acknowledge care plans properly.',
    atRisk: 'Care plan acknowledgment should be obtained consistently.',
    nonCompliant: 'Caregivers should acknowledge reviewing care plans.',
    unknown: 'Verify care plan acknowledgment process.',
  },
};

export const TEMPLATE_F3_EVV: QuestionTemplate = {
  itemId: 'F3',
  templateId: 'evv',
  title: 'Electronic Visit Verification (EVV)',
  description: 'System to verify visit times and locations',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do you use an EVV system for visit verification?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes, fully implemented' },
        { value: 'partial', label: 'Partially implemented' },
        { value: 'no', label: 'No EVV system' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'EVV system is properly implemented.',
    atRisk: 'EVV implementation needs to be completed.',
    nonCompliant: 'EVV is required by federal law for Medicaid-funded services.',
    unknown: 'Verify EVV requirements for your services.',
  },
};

export const TEMPLATE_F4_MISSED_VISITS: QuestionTemplate = {
  itemId: 'F4',
  templateId: 'missed-visits',
  title: 'Missed Visit Reporting',
  description: 'Protocol for handling missed or late visits',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do you have a documented process for reporting and following up on missed visits?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes, documented and followed' },
        { value: 'informal', label: 'Informal process' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'informal', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Missed visit reporting process is documented.',
    atRisk: 'Formalize your missed visit reporting process.',
    nonCompliant: 'A missed visit protocol protects clients and your agency.',
    unknown: 'Verify missed visit reporting process.',
  },
};

export const TEMPLATE_F5_SUPERVISION: QuestionTemplate = {
  itemId: 'F5',
  templateId: 'supervisory-visits',
  title: 'Supervisory Visits',
  description: 'Regular supervisor check-ins with clients and staff',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do you conduct and document supervisory visits per {state} requirements?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes, conducted and documented' },
        { value: 'sometimes', label: 'Sometimes' },
        { value: 'no', label: 'No' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Supervisory visits are conducted and documented.',
    atRisk: 'Supervisory visits should be conducted consistently.',
    nonCompliant: 'Supervisory visits are required by {state} regulations.',
    unknown: 'Verify supervisory visit requirements.',
  },
};

// ============================================
// SECTION G: DOCUMENTATION INTEGRITY (G1-G5)
// ============================================

export const TEMPLATE_G1_EXPIRATION_TRACKING: QuestionTemplate = {
  itemId: 'G1',
  templateId: 'expiration-tracking',
  title: 'Document Expiration Tracking',
  description: 'System to track license, certification, and clearance expiration',
  severity: 'high',
  questions: [
    {
      id: 'q1',
      question: 'Do you have a system to track document expiration dates?',
      inputType: 'select',
      options: [
        { value: 'automated', label: 'Yes, automated system with alerts' },
        { value: 'manual', label: 'Yes, manual tracking (spreadsheet)' },
        { value: 'no', label: 'No systematic tracking' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'includes', questionId: 'q1', value: ['automated', 'manual'], result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Document expiration tracking system is in place.',
    atRisk: 'Consider upgrading to automated tracking.',
    nonCompliant: 'Expiration tracking prevents compliance gaps. Implement a tracking system.',
    unknown: 'Verify expiration tracking process.',
  },
};

export const TEMPLATE_G2_REMINDERS: QuestionTemplate = {
  itemId: 'G2',
  templateId: 'expiration-reminders',
  title: 'Expiration Reminder Notifications',
  description: 'Alerts before documents expire',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do you send reminder notifications before documents expire?',
      inputType: 'select',
      options: [
        { value: 'automated', label: 'Yes, automated reminders' },
        { value: 'manual', label: 'Yes, manual reminders' },
        { value: 'no', label: 'No reminders sent' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'includes', questionId: 'q1', value: ['automated', 'manual'], result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Expiration reminders are active.',
    atRisk: 'Consider automated reminders for efficiency.',
    nonCompliant: 'Reminders prevent expired documents. Implement a reminder system.',
    unknown: 'Verify reminder notification process.',
  },
};

export const TEMPLATE_G3_AUDIT_READY: QuestionTemplate = {
  itemId: 'G3',
  templateId: 'audit-ready',
  title: 'Audit-Ready Folder Structure',
  description: 'Personnel files organized for surveyor review',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Are your personnel files organized in an audit-ready structure?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes, consistently organized' },
        { value: 'somewhat', label: 'Somewhat organized' },
        { value: 'no', label: 'No standard organization' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'somewhat', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Files are audit-ready.',
    atRisk: 'Improve file organization for faster surveyor access.',
    nonCompliant: 'Organized files are essential for survey readiness.',
    unknown: 'Verify file organization.',
  },
};

export const TEMPLATE_G4_VERSION_CONTROL: QuestionTemplate = {
  itemId: 'G4',
  templateId: 'version-control',
  title: 'Policy Version Control',
  description: 'Track updates to policies and procedures',
  severity: 'low',
  questions: [
    {
      id: 'q1',
      question: 'Do you maintain version control and change logs for policies?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes, with dates and approval signatures' },
        { value: 'partial', label: 'Partially - dates only' },
        { value: 'no', label: 'No version tracking' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Policy version control is maintained.',
    atRisk: 'Add approval signatures to version tracking.',
    nonCompliant: 'Version control helps demonstrate policy governance.',
    unknown: 'Verify policy version control.',
  },
};

export const TEMPLATE_G5_ACCESS_CONTROLS: QuestionTemplate = {
  itemId: 'G5',
  templateId: 'access-controls',
  title: 'Access Controls for Sensitive Records',
  description: 'Only authorized personnel can access files',
  severity: 'medium',
  questions: [
    {
      id: 'q1',
      question: 'Do you have access controls limiting who can view personnel and client files?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes, role-based access controls' },
        { value: 'partial', label: 'Partial - some restrictions' },
        { value: 'no', label: 'No access controls' },
      ],
      required: true,
    },
  ],
  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
    { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
  ],
  reportLanguage: {
    compliant: 'Access controls are properly implemented.',
    atRisk: 'Strengthen access controls for sensitive records.',
    nonCompliant: 'Access controls protect client and employee privacy.',
    unknown: 'Verify access control implementation.',
  },
};

// ============================================
// SECTION A: ADDITIONAL TEMPLATES
// ============================================

export const TEMPLATE_A7_EMERGENCY_PLAN: QuestionTemplate = {
  itemId: 'A7',
  templateId: 'emergency-preparedness',
  title: 'Emergency Preparedness Plan',
  description: 'Agency must have comprehensive emergency and disaster preparedness policies',
  severity: 'medium',

  questions: [
    {
      id: 'q1',
      question: 'Which of the following does your emergency preparedness plan include? (Select all that apply)',
      inputType: 'checklist',
      options: [
        { value: 'contact_list', label: 'Staff emergency contact list/call tree' },
        { value: 'client_contacts', label: 'Client emergency contact information (updated)' },
        { value: 'natural_disaster', label: 'Procedures for natural disasters (hurricane, tornado, flood)' },
        { value: 'utility_failure', label: 'Procedures for utility failures (power outage)' },
        { value: 'client_notification', label: 'Client notification procedures during emergencies' },
        { value: 'continuity', label: 'Continuity of care plan (backup caregivers, alternative arrangements)' },
        { value: 'communication', label: 'Communication plan (how to reach clients/staff when phones/internet down)' },
        { value: 'reviewed', label: 'Plan has been reviewed/updated within the past 12 months' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
    { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
    { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Your emergency preparedness plan is comprehensive and meets {state} requirements.',
    atRisk: 'Your emergency plan is missing key elements. Your plan should address how you will maintain care during emergencies. Add: {missing}.',
    nonCompliant: 'Your agency lacks adequate emergency preparedness planning. This is a required policy under {state} regulations. Develop a comprehensive emergency plan immediately that covers natural disasters, utility failures, and continuity of care.',
    unknown: 'Review your emergency preparedness plan against {state} requirements.',
  },
};

export const TEMPLATE_A8_COMPLAINT_HANDLING: QuestionTemplate = {
  itemId: 'A8',
  templateId: 'complaint-handling',
  title: 'Complaint and Grievance Handling',
  description: 'Agency must have a written complaint and grievance policy',
  severity: 'low',

  questions: [
    {
      id: 'q1',
      question: 'Does your agency have a written complaint and grievance policy?',
      inputType: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unsure', label: 'Not sure' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Does your complaint process include: (Select all that apply)',
      inputType: 'checklist',
      options: [
        { value: 'filing_methods', label: 'How clients can file a complaint (phone, written, in person)' },
        { value: 'timeframe', label: 'Timeframe for acknowledging complaints' },
        { value: 'investigation', label: 'Investigation process' },
        { value: 'documentation', label: 'Documentation of complaints and resolutions' },
        { value: 'state_contact', label: 'Information on how to contact {agencyAbbr} if unsatisfied' },
        { value: 'non_retaliation', label: 'Non-retaliation statement' },
      ],
      showCondition: {
        questionId: 'q1',
        operator: 'equals',
        value: 'yes',
      },
    },
  ],

  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'yes' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6 },
    ], result: 'COMPLIANT' },
    { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
    { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Your complaint and grievance process meets {state} requirements.',
    atRisk: 'Your complaint process is missing: {missing}. Update your policy to ensure clients know how to file complaints and what to expect.',
    nonCompliant: 'You must have a written complaint and grievance policy per {state} regulations. Develop this policy and ensure it\'s provided to all clients at intake.',
    unknown: 'Review your complaint and grievance policy against {state} requirements.',
  },
};

export const TEMPLATE_A9_QA_REVIEWS: QuestionTemplate = {
  itemId: 'A9',
  templateId: 'qa-reviews',
  title: 'Quality Assurance Reviews',
  description: 'Agency must have a quality assurance program that evaluates services',
  severity: 'medium',

  questions: [
    {
      id: 'q1',
      question: 'How often does your agency conduct formal quality assurance reviews?',
      inputType: 'select',
      options: [
        { value: 'monthly', label: 'Monthly' },
        { value: 'quarterly', label: 'Quarterly' },
        { value: 'annually', label: 'Annually' },
        { value: 'irregular', label: 'Irregularly / as needed' },
        { value: 'never', label: 'Never / No formal QA program' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'What does your QA program include? (Select all that apply)',
      inputType: 'checklist',
      options: [
        { value: 'satisfaction', label: 'Client satisfaction surveys or feedback' },
        { value: 'audits', label: 'Chart/record audits' },
        { value: 'evaluations', label: 'Staff performance evaluations' },
        { value: 'trend_analysis', label: 'Incident trend analysis' },
        { value: 'findings', label: 'Documented findings and corrective actions' },
        { value: 'reports', label: 'Written QA reports' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'equals', questionId: 'q1', value: 'never', result: 'NON_COMPLIANT' },
    { condition: 'allTrue', value: [
      { condition: 'includes', questionId: 'q1', value: ['monthly', 'quarterly'] },
      { condition: 'countAtLeast', questionId: 'q2', value: 5 },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'annually' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3 },
    ], result: 'AT_RISK' },
    { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'Your quality assurance program meets {state} requirements.',
    atRisk: 'Your QA program could be strengthened. Consider adding: {missing}. Document all QA activities and findings.',
    nonCompliant: 'You must have a quality assurance program per {state} regulations. Establish a formal QA process that includes regular reviews, documented findings, and corrective action plans.',
    unknown: 'Review your QA program against {state} requirements.',
  },
};

// ============================================
// CALIFORNIA-ONLY TEMPLATES
// ============================================

export const TEMPLATE_CA_TB_CLEARANCE: QuestionTemplate = {
  itemId: 'CA-TB',
  templateId: 'ca-tb-clearance',
  title: 'Tuberculosis Clearance',
  description: 'All HCAs must have TB clearance before employment and every 2 years',
  severity: 'medium',
  applicableStates: ['CA'],

  questions: [
    {
      id: 'q1',
      question: 'Do ALL your Home Care Aides have TB clearance on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all have current TB clearance' },
        { value: 'most', label: 'Most do, but some are missing or expired' },
        { value: 'no', label: 'No / not sure' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'How do you track TB clearance expiration dates?',
      inputType: 'select',
      options: [
        { value: 'system', label: 'System/software tracks and alerts before expiration' },
        { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
        { value: 'periodic', label: 'We check periodically' },
        { value: 'none', label: 'We don\'t actively track expiration' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'all' },
      { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'most' },
      { condition: 'equals', questionId: 'q2', value: 'periodic' },
    ], result: 'AT_RISK' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'no' },
      { condition: 'equals', questionId: 'q2', value: 'none' },
    ], result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'TB clearance documentation is current for all HCAs.',
    atRisk: 'Some TB clearances may be expired. California requires TB clearance every 2 years. Review all HCA files and schedule testing for anyone whose clearance is older than 2 years.',
    nonCompliant: 'HCAs are working without valid TB clearance. This creates health risks and regulatory exposure. Immediately obtain TB testing for all HCAs without current clearance. Do not assign HCAs to clients until cleared.',
    unknown: 'Verify TB clearance status for all HCAs.',
  },
};

export const TEMPLATE_CA_MANDATED_REPORTER: QuestionTemplate = {
  itemId: 'CA-MR',
  templateId: 'ca-mandated-reporter',
  title: 'Mandated Reporter Acknowledgment',
  description: 'All HCAs are mandated reporters and must sign SOC 341A',
  severity: 'high',
  applicableStates: ['CA'],

  questions: [
    {
      id: 'q1',
      question: 'Do ALL your HCAs have a signed SOC 341A (Statement Acknowledging Requirement to Report Suspected Abuse) on file?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, all HCAs' },
        { value: 'most', label: 'Most HCAs' },
        { value: 'no', label: 'No / not sure what this form is' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Do you train HCAs on how to identify and report suspected abuse, neglect, or exploitation?',
      inputType: 'select',
      options: [
        { value: 'orientation_annual', label: 'Yes, during orientation and annually' },
        { value: 'orientation_only', label: 'Yes, during orientation only' },
        { value: 'no', label: 'No formal training' },
        { value: 'unsure', label: 'Not sure' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'allTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'all' },
      { condition: 'includes', questionId: 'q2', value: ['orientation_annual', 'orientation_only'] },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'most' },
      { condition: 'equals', questionId: 'q2', value: 'orientation_only' },
    ], result: 'AT_RISK' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'no' },
      { condition: 'equals', questionId: 'q2', value: 'no' },
    ], result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'All HCAs have acknowledged their mandated reporter duties per HSC 1796.42.',
    atRisk: 'Some mandated reporter acknowledgments may be missing. Per HSC 1796.42, all HCAs must sign SOC 341A. Review files and obtain signatures for any missing acknowledgments.',
    nonCompliant: 'Failure to document mandated reporter acknowledgment violates HSC 1796.42. HCAs who fail to report suspected abuse can face criminal penalties. Immediately obtain signed SOC 341A from all HCAs and provide abuse reporting training.',
    unknown: 'Verify all HCAs have signed SOC 341A and received abuse reporting training.',
  },
};

export const TEMPLATE_CA_HCA_REGISTRY: QuestionTemplate = {
  itemId: 'CA-REG',
  templateId: 'ca-hca-registry',
  title: 'Home Care Aide Registry Compliance',
  description: 'All Home Care Aides must be registered on the CDSS Home Care Aide Registry',
  severity: 'high',
  applicableStates: ['CA'],

  questions: [
    {
      id: 'q1',
      question: 'Are all of your currently employed Home Care Aides registered on the CDSS Home Care Aide Registry?',
      inputType: 'select',
      options: [
        { value: 'all', label: 'Yes, 100% are registered' },
        { value: 'most', label: 'Most are registered, but some are pending' },
        { value: 'some', label: 'Some are not registered' },
        { value: 'unsure', label: 'Not sure' },
        { value: 'none', label: 'We have no HCAs employed currently' },
      ],
      required: true,
    },
    {
      id: 'q2',
      question: 'Do you verify HCA registration status on the public registry BEFORE assigning them to clients?',
      inputType: 'select',
      options: [
        { value: 'always', label: 'Yes, always' },
        { value: 'sometimes', label: 'Sometimes' },
        { value: 'no', label: 'No' },
        { value: 'unsure', label: 'Not sure how to check' },
      ],
      required: true,
    },
  ],

  scoringRules: [
    { condition: 'allTrue', value: [
      { condition: 'includes', questionId: 'q1', value: ['all', 'none'] },
      { condition: 'equals', questionId: 'q2', value: 'always' },
    ], result: 'COMPLIANT' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'most' },
      { condition: 'equals', questionId: 'q2', value: 'sometimes' },
    ], result: 'AT_RISK' },
    { condition: 'anyTrue', value: [
      { condition: 'equals', questionId: 'q1', value: 'some' },
      { condition: 'equals', questionId: 'q2', value: 'no' },
    ], result: 'NON_COMPLIANT' },
  ],

  reportLanguage: {
    compliant: 'All your Home Care Aides are properly registered per HSC 1796.24.',
    atRisk: 'Some HCAs may be pending registration. Per HSC 1796.24, aides cannot provide services until registered. Verify all pending applications and do not assign unregistered aides to clients. Check status at: www.ccld.dss.ca.gov/hcsregistry',
    nonCompliant: 'Using unregistered Home Care Aides is a violation of HSC 1796.24. This can result in fines up to $5,000 per violation. Immediately remove unregistered aides from client assignments and begin the registration process.',
    unknown: 'Verify all HCA registration status at www.ccld.dss.ca.gov/hcsregistry',
  },
};

// ============================================
// EXPORT ALL TEMPLATES
// ============================================

export const ALL_QUESTION_TEMPLATES: QuestionTemplate[] = [
  // Section A: Licensing & Operations
  TEMPLATE_A1_LICENSE_STATUS,
  TEMPLATE_A2_LICENSE_ACCESSIBILITY,
  TEMPLATE_A3_OWNERSHIP_INFO,
  TEMPLATE_A4_POLICIES_PROCEDURES,
  TEMPLATE_A5_SERVICE_AGREEMENTS,
  TEMPLATE_A6_INCIDENT_REPORTING,
  TEMPLATE_A7_EMERGENCY_PLAN,
  TEMPLATE_A8_COMPLAINT_HANDLING,
  TEMPLATE_A9_QA_REVIEWS,
  TEMPLATE_A10_RECORD_RETENTION,
  TEMPLATE_A12_INSURANCE,

  // Section B: Personnel Files
  TEMPLATE_B1_PERSONNEL_FILES,
  TEMPLATE_B2_PHOTO_ID,
  TEMPLATE_B3_JOB_DESCRIPTION,
  TEMPLATE_B4_HIPAA,
  TEMPLATE_B5_CODE_OF_CONDUCT,
  TEMPLATE_B6_ATTENDANCE,
  TEMPLATE_B7_TRAINING_POLICY,
  TEMPLATE_B8_BG_CONSENT,
  TEMPLATE_B9_I9,
  TEMPLATE_B10_TAX_FORMS,

  // Section C: Background Screening
  TEMPLATE_C1_BACKGROUND_CHECK,
  TEMPLATE_C2_BG_RESULTS,
  TEMPLATE_C3_OIG_GSA,
  TEMPLATE_C4_RESCREENING,
  TEMPLATE_C7_DRIVING,

  // Section D: Health & Safety
  TEMPLATE_D1_TB,
  TEMPLATE_D2_HEALTH_CLEARANCE,
  TEMPLATE_D3_CPR,
  TEMPLATE_D4_FIRST_AID,
  TEMPLATE_D5_INFECTION_CONTROL,
  TEMPLATE_D6_BLOODBORNE,
  TEMPLATE_D7_PPE,

  // Section E: Training & Competency
  TEMPLATE_E1_ORIENTATION,
  TEMPLATE_E2_COMPETENCY,
  TEMPLATE_E3_TRAINING_HOURS,
  TEMPLATE_E4_DEMENTIA,
  TEMPLATE_E5_TRANSFER,
  TEMPLATE_E6_MEDICATION,
  TEMPLATE_E7_ABUSE,
  TEMPLATE_E8_CLIENT_RIGHTS,

  // Section F: Care Delivery
  TEMPLATE_F1_CARE_PLANS,
  TEMPLATE_F2_CARE_PLAN_ACK,
  TEMPLATE_F3_EVV,
  TEMPLATE_F4_MISSED_VISITS,
  TEMPLATE_F5_SUPERVISION,

  // Section G: Documentation & Compliance
  TEMPLATE_G1_EXPIRATION_TRACKING,
  TEMPLATE_G2_REMINDERS,
  TEMPLATE_G3_AUDIT_READY,
  TEMPLATE_G4_VERSION_CONTROL,
  TEMPLATE_G5_ACCESS_CONTROLS,

  // California-specific templates
  TEMPLATE_CA_HCA_REGISTRY,
  TEMPLATE_CA_TB_CLEARANCE,
  TEMPLATE_CA_MANDATED_REPORTER,
];

export function getTemplateById(templateId: string): QuestionTemplate | undefined {
  return ALL_QUESTION_TEMPLATES.find(t => t.templateId === templateId || t.itemId === templateId);
}

export function getTemplatesForState(stateCode: string): QuestionTemplate[] {
  return ALL_QUESTION_TEMPLATES.filter(t => {
    // Check if explicitly excluded
    if (t.excludedStates?.includes(stateCode)) return false;
    // Check if only for specific states
    if (t.applicableStates && !t.applicableStates.includes(stateCode)) return false;
    return true;
  });
}
